/**
 * useLogin Hook
 * Provides login functionality with loading and error states
 * Integrates with Zustand store for state management
 */

import { useState, useCallback } from "react";
import { useAuth } from "../providers";
import { useAuthStore as useAuthStoreBase, useAuthLoading, useMfaFactors } from "../store";
import type { LoginCredentials, AuthError } from "../types";

export interface UseLoginResult {
  /** Perform login */
  login: (credentials: LoginCredentials) => Promise<boolean>;
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: AuthError | null;
  /** Whether MFA is required */
  isMFARequired: boolean;
  /** Optional status for non-success login outcomes */
  status?: string | null;
  /** Clear error */
  clearError: () => void;
}

export function useLogin(): UseLoginResult {
  const auth = useAuth();
  const [localError, setLocalError] = useState<AuthError | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const isLoadingFromStore = useAuthLoading();
  const mfaFactorsFromStore = useMfaFactors();

  const login = useCallback(
    async (credentials: LoginCredentials): Promise<boolean> => {
      useAuthStoreBase.getState().setLoading(true);
      useAuthStoreBase.getState().setError(null);
      setLocalError(null);
      setStatus(null);

      try {
        const signInResult = await auth.signIn(credentials);

        if (signInResult?.status === "LOCKED_OUT") {
          setStatus("LOCKED_OUT");
          useAuthStoreBase.getState().setLoading(false);
          return false;
        }

        // Re-read the state after signIn completes to get updated values
        const currentState = useAuthStoreBase.getState();
        
        console.log("🔐 [useLogin] After signIn, state:", {
          authenticated: currentState.authenticated,
          hasUser: !!currentState.user,
          hasMfaFactors: currentState.mfaFactors.length,
        });
        
        // Check if MFA is required from store
        if (currentState.mfaFactors.length > 0) {
          console.log("🔐 [useLogin] MFA required, returning false");
          // setLoading is already handled by setMfaFactors
          return false;
        }

        // Check if user is authenticated (SUCCESS case)
        if (currentState.authenticated && currentState.user) {
          console.log("🔐 [useLogin] User authenticated! Returning true");
          // setLoading is already handled by setLoggedIn
          return true;
        }

        // Check for errors
        if (auth.error) {
          console.error("🔐 [useLogin] Auth error detected:", auth.error);
          const error = auth.error;
          setLocalError(error);
          useAuthStoreBase.getState().setError(error.message);
          useAuthStoreBase.getState().setLoading(false);
          return false;
        }

        useAuthStoreBase.getState().setLoading(false);
        // Return the current authenticated state after signIn
        return currentState.authenticated;
      } catch (err) {
        const authError: AuthError = {
          code: "LOGIN_FAILED",
          message: err instanceof Error ? err.message : "Login failed",
          originalError: err,
        };
        setLocalError(authError);
        useAuthStoreBase.getState().setError(authError.message);
        useAuthStoreBase.getState().setLoading(false);
        return false;
      }
    },
    [auth],
  );

  const clearError = useCallback(() => {
    setLocalError(null);
    useAuthStoreBase.getState().setError(null);
    auth.clearError();
  }, [auth]);

  return {
    login,
    isLoading: isLoadingFromStore || auth.isLoading,
    error: localError ?? auth.error,
    isMFARequired: mfaFactorsFromStore.length > 0,
    status,
    clearError,
  };
}
